import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, API_URL, resolveMediaUrl } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { toEasternArabicNumerals, formatTimeLocale } from '../utils/i18nHelpers';

import ServiceSelection from '../components/booking/ServiceSelection';
import DateSelection from '../components/booking/DateSelection';
import TimeAndArtistSelection from '../components/booking/TimeAndArtistSelection';

function localDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseSlotLocal(dateStr, startTime) {
  if (!dateStr || !startTime) return new Date(NaN);
  const dp = dateStr.trim().slice(0, 10).split('-').map(Number);
  if (dp.length !== 3 || dp.some((n) => Number.isNaN(n))) return new Date(NaN);
  const [y, mo, d] = dp;
  const tp = String(startTime).trim().split(':').map((x) => parseInt(x, 10));
  const h = tp[0] || 0;
  const min = tp[1] || 0;
  const sec = Number.isFinite(tp[2]) ? tp[2] : 0;
  return new Date(y, mo - 1, d, h, min, sec, 0);
}

/* ─── Shared label style ─────────────────────────────────────────────────── */
const stepLabel = (n, text) => (
  <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.8rem', letterSpacing: '0.06em', color: '#ffffff', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
    <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.9rem', fontWeight: 700, color: 'var(--orange)', border: '2px solid var(--orange)', borderRadius: '50%', width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</span>
    {text}
  </h2>
);

function BookingContent() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { showAlert } = useNotification();
  const [searchParams] = useSearchParams();
  const branchId = searchParams.get('branch_id');
  const navigate = useNavigate();
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-GB';

  const [branch, setBranch] = useState(null);
  const [serviceOptions, setServiceOptions] = useState({ standalone_services: [], bundles: [] });
  const [closedDates, setClosedDates] = useState([]);
  const [pastServiceIds, setPastServiceIds] = useState([]);
  const [selectedStandaloneServiceIds, setSelectedStandaloneServiceIds] = useState([]);
  const [selectedBundleIds, setSelectedBundleIds] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateStr, setDateStr] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedArtistId, setSelectedArtistId] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [pastSlotTick, setPastSlotTick] = useState(0);

  useEffect(() => {
    if (dateStr !== localDateKey()) return undefined;
    const id = window.setInterval(() => setPastSlotTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, [dateStr]);

  useEffect(() => {
    if (branchId) {
      const id = parseInt(branchId);
      api.getBranch(id).then(setBranch).catch(() => { });
      api.getBranchServiceOptions(id).then(setServiceOptions).catch(() => setServiceOptions({ standalone_services: [], bundles: [] }));
      api.getBranchClosedDates(id).then(setClosedDates).catch(() => setClosedDates([]));
    }
  }, [branchId]);

  useEffect(() => {
    if (!user || !branchId) {
      setPastServiceIds([]);
      return;
    }
    api.getBookings()
      .then((bookings) => {
        const set = new Set();
        (bookings || []).forEach((b) => {
          (b.service_ids || []).forEach((id) => set.add(id));
        });
        setPastServiceIds(Array.from(set));
      })
      .catch(() => setPastServiceIds([]));
  }, [user, branchId]);

  const { options, servicesById, bundlesById } = useMemo(() => {
    const standalone = serviceOptions.standalone_services || [];
    const bundles = serviceOptions.bundles || [];
    const opts = [];
    standalone.forEach(s => {
      opts.push({
        type: 'service',
        id: s.id,
        name: s.name,
        name_ar: s.name_ar,
        price: s.price,
        duration_minutes: s.duration_minutes,
        service_ids: [s.id],
        category_name: s.category_name,
      });
    });
    bundles.forEach(b => {
      const serviceIds = (b.items || []).map(i => i.service_id);
      const totalPrice = b.bundle_price != null ? b.bundle_price : (b.items || []).reduce((sum, i) => sum + (i.service?.price || 0), 0);
      const totalDuration = b.total_duration_minutes != null ? b.total_duration_minutes : (b.items || []).reduce((sum, i) => sum + (i.service?.duration_minutes || 0), 0);
      opts.push({
        type: 'bundle',
        id: b.id,
        name: b.name,
        price: totalPrice,
        duration_minutes: totalDuration,
        service_ids: serviceIds,
        category_name: 'Bundle',
      });
    });
    const byId = {};
    standalone.forEach(s => { byId[s.id] = s; });
    const bundleMap = {};
    bundles.forEach(b => {
      bundleMap[b.id] = b;
      (b.items || []).forEach(item => {
        if (item.service && !byId[item.service.id]) byId[item.service.id] = item.service;
      });
    });
    return { options: opts, servicesById: byId, bundlesById: bundleMap };
  }, [serviceOptions]);

  const selectedServices = useMemo(() => {
    const bundleServiceIds = selectedBundleIds.flatMap((bundleId) =>
      (bundlesById[bundleId]?.items || []).map((item) => item.service_id)
    );
    const unique = new Set([...selectedStandaloneServiceIds, ...bundleServiceIds]);
    return Array.from(unique);
  }, [selectedStandaloneServiceIds, selectedBundleIds, bundlesById]);

  const toggleOption = (option) => {
    if (option.type === 'service') {
      const id = option.service_ids[0];
      setSelectedStandaloneServiceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    } else {
      setSelectedBundleIds(prev => prev.includes(option.id) ? prev.filter(x => x !== option.id) : [...prev, option.id]);
    }
  };

  /* ── Build next 7 dates (locale-aware) ── */
  const dates = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const val = `${y}-${m}-${d}`;
      const closedInfo = closedDates.find(cd => cd.date === val);
      const dayNum = String(date.getDate()).padStart(2, '0');
      out.push({
        label: i === 0 ? t('booking.today') : date.toLocaleDateString(locale, { weekday: 'short' }),
        value: val,
        display: date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
        day: locale === 'ar-EG' ? toEasternArabicNumerals(dayNum) : dayNum,
        month: date.toLocaleDateString(locale, { month: 'short' }),
        closed: !!closedInfo,
        reason: closedInfo?.reason
      });
    }
    return out;
  }, [closedDates, locale, t]);

  const selectedDateLabel = useMemo(() => {
    if (!selectedDate) return null;
    const fromList = dates.find((d) => d.value === selectedDate)?.display;
    if (fromList) return fromList;
    const parts = selectedDate.split("-").map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return selectedDate;
    const [y, m, d] = parts;
    return new Date(y, m - 1, d).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
  }, [dates, selectedDate, locale]);

  useEffect(() => {
    if (!branchId || selectedServices.length === 0 || !dateStr) {
      setSlots([]); setSelectedTime(null); setSelectedArtistId(null); return;
    }
    setLoadingSlots(true);
    setSelectedTime(null); setSelectedArtistId(null);
    const lang = i18n.language && i18n.language.startsWith('ar') ? 'ar' : null;
    api.getBranchAvailability(parseInt(branchId), selectedServices, dateStr, lang)
      .then(data => {
        setSlots(data);
      })
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [branchId, selectedServices, dateStr, i18n.language]);

  const pricingSummary = useMemo(() => {
    const selectedBundles = selectedBundleIds.map((id) => bundlesById[id]).filter(Boolean);
    const coveredServiceIds = new Set(
      selectedBundles.flatMap((bundle) => (bundle.items || []).map((item) => item.service_id))
    );
    const uncoveredStandaloneServices = selectedStandaloneServiceIds
      .filter((serviceId) => !coveredServiceIds.has(serviceId))
      .map((serviceId) => servicesById[serviceId])
      .filter(Boolean);
    const bundlesTotalPrice = selectedBundles.reduce((sum, bundle) => {
      if (bundle.bundle_price != null) return sum + bundle.bundle_price;
      return sum + (bundle.items || []).reduce((itemSum, item) => itemSum + (item.service?.price || 0), 0);
    }, 0);
    const bundlesTotalDuration = selectedBundles.reduce((sum, bundle) => {
      if (bundle.total_duration_minutes != null) return sum + bundle.total_duration_minutes;
      return sum + (bundle.items || []).reduce((itemSum, item) => itemSum + (item.service?.duration_minutes || 0), 0);
    }, 0);
    const standaloneTotalPrice = uncoveredStandaloneServices.reduce((sum, service) => sum + (service.price || 0), 0);
    const standaloneTotalDuration = uncoveredStandaloneServices.reduce((sum, service) => sum + (service.duration_minutes || 0), 0);
    return {
      totalPrice: bundlesTotalPrice + standaloneTotalPrice,
      totalDuration: bundlesTotalDuration + standaloneTotalDuration,
      selectedBundles,
      uncoveredStandaloneServices,
      combinedName: [
        ...selectedBundles.map((bundle) => bundle.name).filter(Boolean),
        ...uncoveredStandaloneServices.map((service) => service.name).filter(Boolean),
      ].join(' + '),
    };
  }, [selectedBundleIds, bundlesById, selectedStandaloneServiceIds, servicesById]);

  const visibleSlots = useMemo(() => {
    if (!dateStr || !slots.length) return slots;
    if (dateStr !== localDateKey()) return slots;
    const now = Date.now();
    return slots.filter((s) => {
      const t = parseSlotLocal(dateStr, s.start_time);
      return !Number.isNaN(t.getTime()) && t.getTime() > now;
    });
  }, [slots, dateStr, pastSlotTick]);

  useEffect(() => {
    if (!selectedTime) return;
    if (visibleSlots.some((s) => s.start_time === selectedTime)) return;
    setSelectedTime(null);
    setSelectedArtistId(null);
  }, [visibleSlots, selectedTime]);

  const selectedSlot = useMemo(() => {
    if (!selectedTime || selectedArtistId == null) return null;
    return visibleSlots.find(s => s.start_time === selectedTime && s.artist_id === selectedArtistId) || null;
  }, [visibleSlots, selectedTime, selectedArtistId]);

  const handleSubmit = async () => {
    if (selectedServices.length === 0 || !dateStr || !selectedSlot || !branchId) {
      showAlert(t('booking.selectAllRequired')); return;
    }
    setLoading(true);
    try {
      await api.createBooking({
        branch_id: parseInt(branchId),
        service_ids: selectedServices,
        bundle_ids: selectedBundleIds,
        artist_id: selectedSlot.artist_id,
        seat_id: selectedSlot.seat_id, // automatically assigned
        service_name: pricingSummary.combinedName || t('booking.selectService'),
        service_price: pricingSummary.totalPrice,
        service_duration_minutes: pricingSummary.totalDuration || 30,
        booking_date: dateStr,
        booking_time: selectedSlot.start_time,
      });
      navigate('/confirm');
    } catch (err) {
      showAlert(err.message || t('booking.bookingFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time24) => formatTimeLocale(time24, locale);

  const availableTimes = useMemo(() => {
    return [...new Set(visibleSlots.map(s => s.start_time))].filter(Boolean).sort();
  }, [visibleSlots]);

  const artistOptions = useMemo(() => {
    if (!selectedTime) return [];
    const byArtist = {};
    visibleSlots.filter(s => s.start_time === selectedTime).forEach(s => {
      if (!byArtist[s.artist_id]) {
        byArtist[s.artist_id] = {
          id: s.artist_id,
          name: s.artist_name,
          photo: s.artist_photo_url,
          role: s.artist_role || 'Professional',
          rating: s.artist_rating != null ? Number(s.artist_rating) : null,
          rating_count: s.rating_count != null ? Number(s.rating_count) : 0,
        };
      }
    });
    return Object.values(byArtist).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [visibleSlots, selectedTime]);

  const categoryList = useMemo(() => {
    const cats = [...new Set(options.map(o => o.category_name).filter(Boolean))].sort();
    return ['All', ...cats];
  }, [options]);

  const normalizeKey = (s) => (s || '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  const getCategoryLabel = (cat) => {
    if (cat === 'All') return t('booking.allCategories');
    const key = 'booking.categories.' + normalizeKey(cat);
    const out = t(key);
    return out !== key ? out : cat;
  };
  const getArtistRoleLabel = (role) => {
    if (!role) return '';
    const key = 'booking.roles.' + normalizeKey(role);
    const out = t(key);
    return out !== key ? out : role;
  };
  const getServiceDisplayName = (service) => {
    if (!service?.name) return '';
    const key = 'booking.serviceNames.' + normalizeKey(service.name);
    const out = t(key);
    return out !== key ? out : service.name;
  };


  if (!branchId) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto', sans-serif" }}>
        <p style={{ color: 'var(--text-muted)' }}>{t('booking.selectBranchFirst')}</p>
      </div>
    );
  }

  return (
    <div className="booking-page" style={{ minHeight: '100vh', background: 'var(--bg-base)', color: '#ffffff', padding: '7rem 1.5rem 5rem', fontFamily: "'Roboto', sans-serif" }}>
      <div className="booking-inner" style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Page header */}
        <div style={{ marginBottom: '3rem' }}>
          <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            {branch?.name || t('common.loading')}
          </p>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(2.5rem, 6vw, 4rem)', letterSpacing: '0.06em', color: '#ffffff', lineHeight: 1, marginBottom: '0.25rem' }}>{t('booking.bookAppointment')}</h1>
          <div style={{ width: '60px', height: '4px', background: 'var(--orange)', borderRadius: '2px' }} />
        </div>

        <div className="booking-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '4rem', alignItems: 'start' }}>

          {/* ── Left: Steps ── */}
          <div className="booking-steps" style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>

            {/* Step 1: Services */}
            <ServiceSelection
              options={options}
              selectedServices={selectedServices}
              selectedBundleIds={selectedBundleIds}
              toggleOption={toggleOption}
              searchQuery={serviceSearch}
              setSearchQuery={setServiceSearch}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              categoryList={categoryList}
              pastServiceIds={pastServiceIds}
              popularServiceIds={serviceOptions.popular_service_ids || []}
            />

            {/* Step 2: Date */}
            <DateSelection
              dates={dates}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              setDateStr={setDateStr}
              setSelectedTime={setSelectedTime}
              setSelectedArtistId={setSelectedArtistId}
            />

            {/* Step 3 & 4: Select Time & Artist */}
            <TimeAndArtistSelection
              selectedTime={selectedTime}
              setSelectedTime={setSelectedTime}
              setSelectedArtistId={setSelectedArtistId}
              availableTimes={availableTimes}
              formatTime={formatTime}
              artistOptions={artistOptions}
              selectedArtistId={selectedArtistId}
              dateStr={dateStr}
              loadingSlots={loadingSlots}
              slots={visibleSlots}
            />

          </div>

          {/* ── Right: Summary ── */}
          <div className="booking-summary-wrap" style={{ position: 'sticky', top: '6rem' }}>
            <div className="booking-summary-panel" style={{ background: '#18181b', borderRadius: '12px', padding: '1.5rem', boxSizing: 'border-box' }}>

              <h2 style={{ fontFamily: "'Roboto', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.1em', color: '#ffffff', marginBottom: '1.5rem', textTransform: 'uppercase' }}>{t('booking.summary')}</h2>

              {selectedServices.length > 0 && (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #27272a' }}>
                  <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em', color: '#a1a1aa', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('booking.service')}</p>
                  <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '0.95rem', fontWeight: 600, color: '#ffffff', marginBottom: '0.25rem' }}>{pricingSummary.combinedName}</p>
                  <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '0.8rem', color: '#a1a1aa' }}>
                    {locale === 'ar-EG'
                      ? t('booking.durationMin', { minutes: toEasternArabicNumerals(String(pricingSummary.totalDuration)) })
                      : t('booking.durationMin', { minutes: pricingSummary.totalDuration })}
                  </p>
                </div>
              )}

              {selectedArtistId && selectedSlot && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #27272a' }}>
                  <div style={{ width: '45px', height: '45px', borderRadius: '50%', background: '#27272a', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {selectedSlot.artist_photo_url ? (
                      <img src={resolveMediaUrl(selectedSlot.artist_photo_url)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ color: '#a1a1aa', fontSize: '1.2rem', fontFamily: "'Bebas Neue', sans-serif" }}>{selectedSlot.artist_name[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em', color: '#a1a1aa', textTransform: 'uppercase', marginBottom: '0.1rem' }}>{t('booking.artist')}</p>
                    <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '1rem', fontWeight: 600, color: '#ffffff' }}>{selectedSlot.artist_name}</p>
                    <p style={{ fontSize: '0.75rem', color: '#71717a' }}>{getArtistRoleLabel(selectedSlot.artist_role || 'Professional')}</p>
                  </div>
                </div>
              )}

              {selectedDate && selectedTime && selectedDateLabel && (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #27272a' }}>
                  <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em', color: '#a1a1aa', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('booking.dateAndTime')}</p>
                  <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 }}>
                    <span style={{ color: '#ffffff' }}>{selectedDateLabel}</span>
                    <span style={{ color: '#71717a', margin: '0 0.35rem' }}>·</span>
                    <span style={{ color: 'var(--orange)' }}>{formatTime(selectedTime)}</span>
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em', color: '#a1a1aa', textTransform: 'uppercase' }}>{t('booking.total')}</span>
                <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '1.35rem', fontWeight: 700, color: '#ffffff' }}>E£{locale === 'ar-EG' ? toEasternArabicNumerals(pricingSummary.totalPrice.toFixed(2)) : pricingSummary.totalPrice.toFixed(2)}</span>
              </div>

              <p style={{ fontSize: '0.78rem', color: '#a1a1aa', marginBottom: '1rem' }}>{t('profile.noCancellationPolicy')}</p>

              <button
                onClick={handleSubmit}
                disabled={loading || selectedServices.length === 0 || !dateStr || !selectedSlot}
                style={{
                  width: '100%',
                  padding: '0.9rem',
                  background: 'var(--orange)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: (loading || selectedServices.length === 0 || !dateStr || !selectedSlot) ? 'not-allowed' : 'pointer',
                  opacity: (loading || selectedServices.length === 0 || !dateStr || !selectedSlot) ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {loading ? t('booking.processing') : t('booking.confirmBookingBtn')}
              </button>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Booking() {
  return (
    <BookingContent />
  );
}
